import { Case, Proposal, Verifier, VerifierResult, Being, Device, Hideout, Affinity } from './types';
// Import weeks 40-52 (2025) and 1-21 (2026) JSON files
import V2025_40 from './V2025-40.json';
import V2025_41 from './V2025-41.json';
import V2025_42 from './V2025-42.json';
import V2025_43 from './V2025-43.json';
import V2025_44 from './V2025-44.json';
import V2025_45 from './V2025-45.json';
import V2025_46 from './V2025-46.json';
import V2025_47 from './V2025-47.json';
import V2025_48 from './V2025-48.json';
import V2025_49 from './V2025-49.json';
import V2025_50 from './V2025-50.json';
import V2025_51 from './V2025-51.json';
import V2025_52 from './V2025-52.json';
import V2026_01 from './V2026-01.json';
import V2026_02 from './V2026-02.json';
import V2026_03 from './V2026-03.json';
import V2026_04 from './V2026-04.json';
import V2026_05 from './V2026-05.json';
import V2026_06 from './V2026-06.json';
import V2026_07 from './V2026-07.json';
import V2026_08 from './V2026-08.json';
import V2026_09 from './V2026-09.json';
import V2026_10 from './V2026-10.json';
import V2026_11 from './V2026-11.json';
import V2026_12 from './V2026-12.json';
import V2026_13 from './V2026-13.json';
import V2026_14 from './V2026-14.json';
import V2026_15 from './V2026-15.json';
import V2026_16 from './V2026-16.json';
import V2026_17 from './V2026-17.json';
import V2026_18 from './V2026-18.json';
import V2026_19 from './V2026-19.json';
import V2026_20 from './V2026-20.json';
import V2026_21 from './V2026-21.json';

console.log('🔥🔥🔥 gameLogic.ts is loading... 🔥🔥🔥');
console.log('🚨🚨🚨 NEW DEBUG MESSAGE ADDED 🚨🚨🚨');
console.log('🎯🎯🎯 LOCATION FIX IS ACTIVE 🎯🎯🎯');

// Dynamic case loading system
interface CaseFile {
  yearWeek: string;
  cases: Case[] | { casefile_id?: string; cases: Case[] };
}

// Helper function to map "Device" to "Weapon" in clue descriptions
function mapDeviceToWeaponInClues(verifiers: any[]): any[] {
  if (!Array.isArray(verifiers)) return verifiers;
  
  return verifiers.map(verifier => ({
    ...verifier,
    Description: verifier.Description ? verifier.Description.replace(/Device/g, 'Weapon') : verifier.Description,
    Negative: verifier.Negative ? verifier.Negative.replace(/Device/g, 'Weapon') : verifier.Negative,
    Tooltip: verifier.Tooltip ? verifier.Tooltip.replace(/Device/g, 'Weapon') : verifier.Tooltip
  }));
}

// Helper function to process imported JSON data with format detection
function processImportedFile(data: any): any {
  console.log(`processImportedFile: Processing data:`, {
    isArray: Array.isArray(data),
    length: Array.isArray(data) ? data.length : 'N/A',
    hasCasefileId: !!data.casefile_id,
    hasCases: !!data.cases,
    dataKeys: Object.keys(data),
    firstCaseId: Array.isArray(data) ? data[0]?.case_id : 'N/A'
  });
  
  // Handle both old format (direct array) and new format (with casefile_id and cases)
  if (data.casefile_id && data.cases) {
    // New format: return the data as-is
    console.log(`processImportedFile: Using new format, cases length:`, data.cases.length);
    // Debug: Check if first case has incident.location
    if (data.cases[0]?.incident) {
      console.log(`processImportedFile: First case incident:`, {
        hasLocation: !!data.cases[0].incident.location,
        location: data.cases[0].incident.location,
        fullIncident: data.cases[0].incident
      });
    }
    return data;
  } else if (Array.isArray(data)) {
    // Old format: wrap in a structure that matches new format
    console.log(`processImportedFile: Using old format, array length:`, data.length);
    const wrapped = { cases: data };
    console.log(`processImportedFile: Wrapped result:`, {
      hasCases: !!wrapped.cases,
      casesLength: wrapped.cases.length,
      firstCaseId: wrapped.cases[0]?.case_id
    });
    return wrapped;
  } else {
    // Fallback: return as-is
    console.log(`processImportedFile: Using fallback format`);
    return data;
  }
}

// Available case files - using direct imports instead of require()
console.log('🚀 Starting CASE_FILES initialization...');
console.log('⏰ Cache bust timestamp:', new Date().toISOString());
console.log('📁 V2025_42 data check (first case):', V2025_42?.cases?.[0]?.incident?.location || 'NO INCIDENT OBJECT');
console.log('📁 V2025_42 data check (second case):', V2025_42?.cases?.[1]?.incident?.location || 'NO LOCATION FOUND');
// Find the 20251017 case specifically
const case20251017 = V2025_42?.cases?.find(c => c.case_id === '20251017');
console.log('📁 V2025_42 case 20251017:', case20251017);
console.log('📁 V2025_42 case 20251017 incident:', case20251017?.incident);
console.log('📁 V2025_42 case 20251017 location:', case20251017?.incident?.location);
console.log('📁 V2025_42 structure:', {
  hasCases: !!V2025_42?.cases,
  casesLength: V2025_42?.cases?.length,
  firstCase: V2025_42?.cases?.[0],
  firstCaseIncident: V2025_42?.cases?.[0]?.incident
});

const CASE_FILES: CaseFile[] = [
  { yearWeek: '2025-40', cases: processImportedFile(V2025_40) },
  { yearWeek: '2025-41', cases: processImportedFile(V2025_41) },
  { yearWeek: '2025-42', cases: processImportedFile(V2025_42) },
  { yearWeek: '2025-43', cases: processImportedFile(V2025_43) },
  { yearWeek: '2025-44', cases: processImportedFile(V2025_44) },
  { yearWeek: '2025-45', cases: processImportedFile(V2025_45) },
  { yearWeek: '2025-46', cases: processImportedFile(V2025_46) },
  { yearWeek: '2025-47', cases: processImportedFile(V2025_47) },
  { yearWeek: '2025-48', cases: processImportedFile(V2025_48) },
  { yearWeek: '2025-49', cases: processImportedFile(V2025_49) },
  { yearWeek: '2025-50', cases: processImportedFile(V2025_50) },
  { yearWeek: '2025-51', cases: processImportedFile(V2025_51) },
  { yearWeek: '2025-52', cases: processImportedFile(V2025_52) },
  { yearWeek: '2026-01', cases: processImportedFile(V2026_01) },
  { yearWeek: '2026-02', cases: processImportedFile(V2026_02) },
  { yearWeek: '2026-03', cases: processImportedFile(V2026_03) },
  { yearWeek: '2026-04', cases: processImportedFile(V2026_04) },
  { yearWeek: '2026-05', cases: processImportedFile(V2026_05) },
  { yearWeek: '2026-06', cases: processImportedFile(V2026_06) },
  { yearWeek: '2026-07', cases: processImportedFile(V2026_07) },
  { yearWeek: '2026-08', cases: processImportedFile(V2026_08) },
  { yearWeek: '2026-09', cases: processImportedFile(V2026_09) },
  { yearWeek: '2026-10', cases: processImportedFile(V2026_10) },
  { yearWeek: '2026-11', cases: processImportedFile(V2026_11) },
  { yearWeek: '2026-12', cases: processImportedFile(V2026_12) },
  { yearWeek: '2026-13', cases: processImportedFile(V2026_13) },
  { yearWeek: '2026-14', cases: processImportedFile(V2026_14) },
  { yearWeek: '2026-15', cases: processImportedFile(V2026_15) },
  { yearWeek: '2026-16', cases: processImportedFile(V2026_16) },
  { yearWeek: '2026-17', cases: processImportedFile(V2026_17) },
  { yearWeek: '2026-18', cases: processImportedFile(V2026_18) },
  { yearWeek: '2026-19', cases: processImportedFile(V2026_19) },
  { yearWeek: '2026-20', cases: processImportedFile(V2026_20) },
  { yearWeek: '2026-21', cases: processImportedFile(V2026_21) },
];

console.log('CASE_FILES initialization completed. Array length:', CASE_FILES.length);

// Helper: get ISO week info (weeks start Monday; week 1 is the week with Jan 4)
function getISOWeekInfo(date: Date): { isoYear: number; isoWeek: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7; // 1..7 (Mon..Sun)
  d.setUTCDate(d.getUTCDate() + 4 - day); // Move to Thursday of current week
  const isoYear = d.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { isoYear, isoWeek: week };
}

// Helper: get ISO year-week string (YYYY-WW)
function getYearWeekFromDate(date: Date): string {
  const { isoYear, isoWeek } = getISOWeekInfo(date);
  return `${isoYear}-${isoWeek.toString().padStart(2, '0')}`;
}

// Helper function to parse date string (handles both ISO and standard formats)
function parseDateString(dateString: string): Date {
  if (/^\d{8}$/.test(dateString)) {
    // ISO format like "20250804" - FIXED: Use UTC to avoid timezone issues
    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6)) - 1;
    const day = parseInt(dateString.substring(6, 8));
    return new Date(Date.UTC(year, month, day));
  } else {
    // Standard date format
    return new Date(dateString);
  }
}

// Load all cases from all available files
export function loadAllCases(): Case[] {
  const allCases: Case[] = [];
  console.log('=== loadAllCases DEBUG ===');
  console.log('CASE_FILES length:', CASE_FILES.length);
  
  CASE_FILES.forEach((file, index) => {
    console.log(`Processing file ${index}: ${file.yearWeek}`);
    console.log('file.cases type:', typeof file.cases);
    console.log('file.cases is array:', Array.isArray(file.cases));
    console.log('file.cases keys:', file.cases && typeof file.cases === 'object' ? Object.keys(file.cases) : 'N/A');
    
    // Get the cases array from the loaded file
    let casesArray: any[];
    if (Array.isArray(file.cases)) {
      // Old format: cases is directly an array
      casesArray = file.cases;
      console.log(`Using old format for ${file.yearWeek}, cases count:`, casesArray.length);
    } else if (file.cases && typeof file.cases === 'object' && 'cases' in file.cases) {
      // New format: cases is an object with a cases property
      console.log(`New format detected for ${file.yearWeek}:`, {
        hasCasesProperty: 'cases' in file.cases,
        casesType: typeof file.cases.cases,
        casesIsArray: Array.isArray(file.cases.cases),
        casesLength: file.cases.cases ? file.cases.cases.length : 'undefined'
      });
      casesArray = file.cases.cases || [];
      console.log(`Using new format for ${file.yearWeek}, cases count:`, casesArray.length);
    } else {
      // Fallback: empty array
      casesArray = [];
      console.log(`Using fallback for ${file.yearWeek}, cases count:`, casesArray.length);
    }
    
    casesArray.forEach((caseData: any) => {
      // Transform the case data to match the expected structure
      const transformedCase = {
        case_id: caseData.case_id || caseData.ID,
            incident: {
      date: caseData.date || caseData.Date,
      type: 'Supernatural' as const,
      description: caseData.description || caseData.Story || caseData.Description,
      headline: caseData.headline || caseData.Headline || caseData.Title,
      brief_headline: caseData.brief_headline,
      brief_description: caseData.brief_description,
      location: caseData.incident?.location || caseData.location,
      Stars: caseData.incident?.Stars, // v2: Incident star rating
      Affinity: caseData.incident?.Affinity as Affinity | undefined // v2: Incident affinity
    },
        components: {
          beings: caseData.beings || caseData.Beings || [],
          devices: caseData.devices || caseData.Devices || [],
          hideouts: caseData.hideouts || caseData.Hideouts || []
        },
        solution: (() => {
          const solution = caseData.solution || caseData.Solution;
          if (!solution) {
            // Return a default solution if none exists
            return {
              being: 'Unknown',
              device: 'Unknown',
              hideout: 'Unknown'
            };
          }
          
          // Handle different solution structures
          if (solution.Being && typeof solution.Being === 'object') {
            // New structure: solution.Being is an object with Name property
            return {
              being: solution.Being.Name,
              device: solution.Device.Name,
              hideout: solution.Hideout.Name
            };
          } else if (solution.being || solution.Being) {
            // Old structure: solution.being is just a string name
            return {
              being: solution.being || solution.Being,
              device: solution.device || solution.Device,
              hideout: solution.hideout || solution.Hideout
            };
          }
          // Return a default solution if structure is unknown
          return {
            being: 'Unknown',
            device: 'Unknown',
            hideout: 'Unknown'
          };
        })(),
        verifiers: mapDeviceToWeaponInClues(caseData.verifiers || caseData.Verifiers || []),
        solution_text: caseData.solution_text || caseData.solutionText,
        yearWeek: file.yearWeek
      };
      allCases.push(transformedCase);
    });
    console.log(`After processing ${file.yearWeek}: total cases so far:`, allCases.length);
  });
  console.log('Final total cases:', allCases.length);
  return allCases;
}

// Get cases for a specific year-week
export function getCasesForYearWeek(yearWeek: string): Case[] {
  const file = CASE_FILES.find(f => f.yearWeek === yearWeek);
  if (!file) {
    console.warn(`No cases found for year-week: ${yearWeek}`);
    return [];
  }
  
  // Get the cases array from the loaded file
  let casesArray: any[];
  if (Array.isArray(file.cases)) {
    // Old format: cases is directly an array
    casesArray = file.cases;
  } else if (file.cases && typeof file.cases === 'object' && 'cases' in file.cases) {
    // New format: cases is an object with a cases property
    console.log(`getCasesForYearWeek new format for ${yearWeek}:`, {
      hasCasesProperty: 'cases' in file.cases,
      casesType: typeof file.cases.cases,
      casesIsArray: Array.isArray(file.cases.cases),
      casesLength: file.cases.cases ? file.cases.cases.length : 'undefined'
    });
    casesArray = file.cases.cases || [];
  } else {
    // Fallback: empty array
    casesArray = [];
  }
  
  return casesArray.map((caseData: any) => {
    // Transform the case data to match the expected structure
    return {
      case_id: caseData.case_id || caseData.ID,
      incident: {
        date: caseData.date || caseData.Date,
        type: 'Supernatural' as const,
        description: caseData.description || caseData.Story || caseData.Description,
        headline: caseData.headline || caseData.Headline || caseData.Title,
        brief_headline: caseData.brief_headline,
        brief_description: caseData.brief_description,
        location: caseData.incident?.location || caseData.location,
        Stars: caseData.incident?.Stars, // v2: Incident star rating
        Affinity: caseData.incident?.Affinity as Affinity | undefined // v2: Incident affinity
      },
      components: {
        beings: caseData.beings || caseData.Beings || [],
        devices: caseData.devices || caseData.Devices || [],
        hideouts: caseData.hideouts || caseData.Hideouts || []
      },
          solution: (() => {
      const solution = caseData.solution || caseData.Solution;
      if (!solution) {
        // Return a default solution if none exists
        return {
          being: 'Unknown',
          device: 'Unknown',
          hideout: 'Unknown'
        };
      }
      
      // Handle different solution structures
      if (solution.Being && typeof solution.Being === 'object') {
        // New structure: solution.Being is an object with Name property
        return {
          being: solution.Being.Name,
          device: solution.Device.Name,
          hideout: solution.Hideout.Name
        };
      } else if (solution.being || solution.Being) {
        // Old structure: solution.being is just a string name
        return {
          being: solution.being || solution.Being,
          device: solution.device || solution.Device,
          hideout: solution.hideout || solution.Hideout
        };
      }
      // Return a default solution if structure is unknown
      return {
        being: 'Unknown',
        device: 'Unknown',
        hideout: 'Unknown'
      };
    })(),
    verifiers: mapDeviceToWeaponInClues(caseData.verifiers || caseData.Verifiers || []),
    solution_text: caseData.solution_text || caseData.solutionText,
    yearWeek: file.yearWeek
    };
  });
}

// Get the default case (for today's date)
export function getDefaultCase(): Case | null {
  const today = new Date();
  const todayString = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  const todayStringISO = today.getFullYear().toString() + 
    (today.getMonth() + 1).toString().padStart(2, '0') + 
    today.getDate().toString().padStart(2, '0'); // Format: YYYYMMDD
  const yearWeek = getYearWeekFromDate(today);
  const cases = getCasesForYearWeek(yearWeek);
  
  if (cases.length === 0) {
    // Fallback to most recent available year-week
    const availableYearWeeks = CASE_FILES.map(f => f.yearWeek).sort().reverse();
    if (availableYearWeeks.length > 0) {
      return getRandomCaseFromYearWeek(availableYearWeeks[0]);
    }
    return null;
  }
  
  // Try to find a case that matches today's exact date in either format
  const todayCase = cases.find(c => 
    c.incident.date === todayString || 
    c.incident.date === todayStringISO
  );
  if (todayCase) {
    return todayCase;
  }
  
  // If no exact match, fall back to random case from the week
  return getRandomCaseFromYearWeek(yearWeek);
}

// Get a random case from a specific year-week
export function getRandomCaseFromYearWeek(yearWeek: string): Case | null {
  const cases = getCasesForYearWeek(yearWeek);
  if (cases.length === 0) return null;
  
  const randomIndex = Math.floor(Math.random() * cases.length);
  return cases[randomIndex];
}

// Get a case by date string
export function getCaseByDate(dateString: string): Case | null {
  const date = parseDateString(dateString);
  const dateStringFormatted = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  const dateStringISO = date.getFullYear().toString() + 
    (date.getMonth() + 1).toString().padStart(2, '0') + 
    date.getDate().toString().padStart(2, '0'); // Format: YYYYMMDD
  
  // Search across ALL cases instead of just the week
  const allCases = loadAllCases();
  
  if (allCases.length === 0) {
    return null;
  }
  
  // PRIORITY: Filter out test cases to ensure real data takes precedence
  const realCases = allCases.filter(c => 
    !c.case_id.startsWith('C-TEST-') && 
    !(c.yearWeek && c.yearWeek.startsWith('TEST-'))
  );
  
  // Try to find a case that matches either date format (prioritizing real cases)
  const exactCase = realCases.find(c => {
    // Normalize both dates for comparison
    const caseDate = c.date || c.incident?.date;
    if (!caseDate) return false;
    
    // Convert case date to standard format for comparison
    let normalizedCaseDate: string;
    if (/^\d{8}$/.test(caseDate)) {
      // Convert YYYYMMDD to YYYY-MM-DD
      const year = caseDate.substring(0, 4);
      const month = caseDate.substring(4, 6);
      const day = caseDate.substring(6, 8);
      normalizedCaseDate = `${year}-${month}-${day}`;
    } else {
      normalizedCaseDate = caseDate;
    }
    
    const matches = normalizedCaseDate === dateStringFormatted || 
                   caseDate === dateStringISO ||
                   caseDate === dateStringFormatted;
    
    return matches;
  });
  
  if (exactCase) {
    return exactCase;
  }
  
  // Fallback: If no real case found, search test cases (but log a warning)
  const testCase = allCases.find(c => 
    (c.case_id.startsWith('C-TEST-') || (c.yearWeek && c.yearWeek.startsWith('TEST-'))) &&
    c.incident.date && (
      c.incident.date === dateStringFormatted ||
      c.incident.date === dateStringISO ||
      c.incident.date === dateString
    )
  );
  
  if (testCase) {
    console.warn(`⚠️  Using test case ${testCase.case_id} for date ${dateString} - this should not happen in production!`);
    return testCase;
  }
  
  // If no exact match, try to find the closest chronological case
  const targetDate = parseDateString(dateString);
  const sortedCases = allCases.sort((a, b) => {
    const dateA = parseDateString(a.incident.date);
    const dateB = parseDateString(b.incident.date);
    return dateA.getTime() - dateB.getTime();
  });
  
  // Find the case with the closest date to our target
  let closestCase = sortedCases[0];
  let minDiff = Math.abs(targetDate.getTime() - parseDateString(closestCase.incident.date).getTime());
  
  for (const caseItem of sortedCases) {
    const caseDate = parseDateString(caseItem.incident.date);
    const diff = Math.abs(targetDate.getTime() - caseDate.getTime());
    if (diff < minDiff) {
      minDiff = diff;
      closestCase = caseItem;
    }
  }
  
  return closestCase;
}

// Legacy function for backward compatibility
export function loadCases(): Case[] {
  return loadAllCases();
}

// Legacy function for backward compatibility
export function getRandomCase(): Case | null {
  return getDefaultCase();
}

// Safe string comparison (case-insensitive, trimmed)
export function safeEquals(a: string, b: string): boolean {
  return (a || '').toLowerCase().trim() === (b || '').toLowerCase().trim();
}

// Evaluate a verifier expression with lowercase affinity standard
export const evaluateVerifier = (
  verifier: Verifier,
  being: Being,
  device: Device,
  hideout: Hideout
): boolean => {
  try {
    // Create evaluation context with lowercase affinities (expressions use lowercase)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const context = {
      being: {
        Stars: being.Stars,
        Affinity: being.Affinity.toLowerCase(), // Convert to lowercase for expressions
        Name: being.Name.toLowerCase()
      },
      device: {
        Stars: device.Stars,
        Affinity: device.Affinity.toLowerCase(), // Convert to lowercase for expressions
        Name: device.Name.toLowerCase()
      },
      hideout: {
        Stars: hideout.Stars,
        Affinity: hideout.Affinity.toLowerCase(), // Convert to lowercase for expressions
        Name: hideout.Name.toLowerCase()
      }
    };

    // Use eval with context - expressions expect lowercase affinities
    // eslint-disable-next-line no-eval
    return eval(verifier.Expression);
  } catch (error) {
    console.error('Error evaluating verifier:', error, verifier);
    return false;
  }
};

// Function removed - using the updated version above

// Check if a proposal matches the solution using agreement logic
export const checkProposal = (
  proposal: Proposal,
  caseData: Case
): VerifierResult[] => {
  const being = caseData.components.beings.find(b => safeEquals(b.Name, proposal.being));
  const device = caseData.components.devices.find(d => safeEquals(d.Name, proposal.device));
  const hideout = caseData.components.hideouts.find(h => safeEquals(h.Name, proposal.hideout));

  if (!being || !device || !hideout) {
    throw new Error('Invalid proposal: component not found');
  }

  // Get the solution components
  const solutionBeing = caseData.components.beings.find(b => safeEquals(b.Name, caseData.solution.being));
  const solutionDevice = caseData.components.devices.find(d => safeEquals(d.Name, caseData.solution.device));
  const solutionHideout = caseData.components.hideouts.find(h => safeEquals(h.Name, caseData.solution.hideout));

  if (!solutionBeing || !solutionDevice || !solutionHideout) {
    throw new Error('Invalid solution: component not found');
  }

  // For each verifier, return true if proposal and solution agree, false otherwise
  return caseData.verifiers.map(verifier => {
    const proposalResult = evaluateVerifier(verifier, being, device, hideout);
    const solutionResult = evaluateVerifier(verifier, solutionBeing, solutionDevice, solutionHideout);
    return {
      verifier,
      result: proposalResult === solutionResult // agreement logic
    };
  });
};

// Check if a proposal is the correct solution
export const isCorrectSolution = (proposal: Proposal, caseData: Case): boolean => {
  return safeEquals(proposal.being, caseData.solution.being) &&
         safeEquals(proposal.device, caseData.solution.device) &&
         safeEquals(proposal.hideout, caseData.solution.hideout);
};

// Get affinity emoji
export const getAffinityEmoji = (affinity: string): string => {
  switch (affinity) {
    case 'Earth': return '🌍';
    case 'Mars': return '🔴';
    case 'Venus': return '🟡';
    case 'Saturn': return '🪐';
    default: return '⭐';
  }
};

// Get device type emoji
export const getDeviceTypeEmoji = (type: string): string => {
  switch (type) {
    case 'Dimensional/Temporal Anomalies': return '🌀';
    case 'Death and Destruction': return '💀';
    case 'Extraterrestrial Phenomena': return '👽';
    case 'Supernatural': return '👻';
    case 'Psychic / Mental': return '🧠';
    default: return '⚡';
  }
};

// Get being type emoji
export const getBeingTypeEmoji = (type: string): string => {
  switch (type) {
    case 'echo': return '👤';
    case 'mutation': return '🧬';
    case 'entity': return '👹';
    case 'oracle': return '🔮';
    case 'ghost': return '👻';
    case 'construct': return '🤖';
    case 'parasite': return '🦠';
    case 'emissary': return '👁️';
    case 'artifact': return '🏺';
    case 'wraith': return '💨';
    default: return '👽';
  }
};

// Render stars
export const renderStars = (count: number): string => {
  return '★'.repeat(count);
};

// Enhance clue text with incident attribute values in brackets
export const enhanceClueWithIncidentValues = (clueText: string, caseData: Case): string => {
  if (!clueText || !caseData?.incident) {
    return clueText;
  }

  let enhancedText = clueText;
  const incident = caseData.incident;

  // Add incident flux value if mentioned and not already present
  if (incident.Stars !== undefined) {
    // First check for "incident flux rating" (longer phrase) - add brackets after "rating"
    const fluxRatingRegex = /incident flux rating(?!\s*\([^)]+\))/gi;
    enhancedText = enhancedText.replace(fluxRatingRegex, (match) => {
      return `${match} (${incident.Stars})`;
    });
    
    // Then check for "incident flux" (without "rating") - replace with "incident flux rating" and add brackets
    // Use negative lookahead to ensure "rating" doesn't follow and brackets aren't already present
    const fluxRegex = /incident flux(?!\s+rating)(?!\s*\([^)]+\))/gi;
    enhancedText = enhancedText.replace(fluxRegex, (match) => {
      return `incident flux rating (${incident.Stars})`;
    });
  }

  // Add incident affinity value if mentioned and not already present
  if (incident.Affinity) {
    // Match "incident affinity" that isn't already followed by a bracket pattern like " (value)"
    const affinityRegex = /incident affinity(?!\s*\([^)]+\))/gi;
    enhancedText = enhancedText.replace(affinityRegex, (match) => {
      return `${match} (${incident.Affinity})`;
    });
  }

  // Add incident location value if mentioned and not already present
  if (incident.location) {
    // First, handle cases where "Incident" (without "Location") is mentioned with letters
    // Replace "Incident" with "Incident Location" when it appears before letter-related text
    // Pattern: "Incident" (not "Incident Location") followed by text that mentions letters
    // Examples: "Incident and Weapon end letters", "Incident location and Being start letters"
    // Use a lookahead to check if letter-related words appear in the rest of the text
    const incidentWithLettersRegex = /\bIncident\b(?!\s+Location)(?=[^.]*\b(?:letter|letters|alphabetically)\b)/gi;
    enhancedText = enhancedText.replace(incidentWithLettersRegex, (match) => {
      return 'Incident Location';
    });
    
    // Match "incident location" that isn't already followed by a bracket pattern like " (value)"
    const locationRegex = /incident location(?!\s*\([^)]+\))/gi;
    enhancedText = enhancedText.replace(locationRegex, (match) => {
      return `${match} (${incident.location})`;
    });
  }

  return enhancedText;
}; 